import React, { useState, useEffect, useCallback } from 'react';
import './AdminPanel.css';

const SERVICES = ['Past Predictions', 'Future Consultation', 'Remedies', 'General'];
const TYPES = ['text', 'audio', 'video'];

const AdminReviewsPage = () => {
  const [key, setKey] = useState(() => sessionStorage.getItem('vtd_admin_key') || '');
  const [authed, setAuthed] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    clientName: '', service: 'General', rating: 5, type: 'text',
    content: '', fileUrl: '', videoUrl: '',
  });
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    if (!key) return;
    setLoading(true);
    fetch('/api/reviews/admin/all', { headers: { 'x-admin-key': key } })
      .then(r => r.json())
      .then(d => {
        if (d.success) { setReviews(d.reviews); setAuthed(true); sessionStorage.setItem('vtd_admin_key', key); }
        else { setAuthed(false); setMsg('Wrong admin key'); }
      })
      .catch(() => setMsg('Network error'))
      .finally(() => setLoading(false));
  }, [key]);

  useEffect(() => { if (key) load(); }, [key, load]);

  const handleAdd = (e) => {
    e.preventDefault();
    setMsg('');
    fetch('/api/reviews/admin/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-admin-key': key },
      body: JSON.stringify(form),
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setForm({ clientName: '', service: 'General', rating: 5, type: 'text', content: '', fileUrl: '', videoUrl: '' });
          setMsg('Review added ✓');
          load();
        } else setMsg(d.error || 'Failed to add');
      });
  };

  const handleApprove = (id) => {
    fetch(`/api/reviews/admin/${id}/approve`, {
      method: 'POST',
      headers: { 'x-admin-key': key },
    }).then(() => load());
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this review?')) return;
    fetch(`/api/reviews/admin/${id}`, {
      method: 'DELETE',
      headers: { 'x-admin-key': key },
    }).then(() => load());
  };

  if (!authed) {
    return (
      <div className="adm">
        <div className="adm__login">
          <h1>Admin · Reviews</h1>
          <p className="adm__sub">Enter admin key to manage reviews.</p>
          <input
            type="password"
            placeholder="Admin key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
          />
          <button onClick={load} disabled={loading}>{loading ? 'Checking…' : 'Unlock'}</button>
          {msg && <p className="adm__err">{msg}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="adm">
      <header className="adm__header">
        <h1>Admin · Reviews ({reviews.length})</h1>
        <a className="adm__nav" href="/admin/community">→ Community</a>
      </header>

      <section className="adm__card">
        <h2>Add a Review</h2>
        <form className="adm__form" onSubmit={handleAdd}>
          <input placeholder="Client name" required
            value={form.clientName} onChange={(e) => setForm({ ...form, clientName: e.target.value })} />
          <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}>
            {SERVICES.map(s => <option key={s}>{s}</option>)}
          </select>
          <div className="adm__row">
            <select value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })}>
              {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} ★</option>)}
            </select>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          {form.type === 'text' && (
            <textarea placeholder="Review text" rows={3} required
              value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
          )}
          {form.type === 'audio' && (
            <input placeholder="Audio file URL (e.g. https://...)" value={form.fileUrl}
              onChange={(e) => setForm({ ...form, fileUrl: e.target.value })} />
          )}
          {form.type === 'video' && (
            <input placeholder="Video URL (YouTube link works)" value={form.videoUrl}
              onChange={(e) => setForm({ ...form, videoUrl: e.target.value })} />
          )}
          <button type="submit">Add Review</button>
          {msg && <span className="adm__msg">{msg}</span>}
        </form>
      </section>

      <section className="adm__card">
        <h2>All Reviews</h2>
        {!reviews.length && <p className="adm__sub">No reviews yet.</p>}
        <ul className="adm__list">
          {reviews.map(r => (
            <li key={r.id} className={`adm-item${r.approved ? '' : ' adm-item--draft'}`}>
              <div className="adm-item__main">
                <div className="adm-item__head">
                  <strong>{r.clientName}</strong>
                  <span className="adm-item__svc">{r.service}</span>
                  <span className="adm-item__rating">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                  <span className="adm-item__type">{r.type}</span>
                </div>
                {r.type === 'text' && <p className="adm-item__text">"{r.content}"</p>}
                {r.type === 'audio' && <a href={r.fileUrl}>🎤 Audio</a>}
                {r.type === 'video' && <a href={r.videoUrl}>▶ Video</a>}
                <p className="adm-item__date">{new Date(r.timestamp).toLocaleString('en-IN')}</p>
              </div>
              <div className="adm-item__actions">
                <button onClick={() => handleApprove(r.id)}>
                  {r.approved ? 'Unpublish' : 'Approve'}
                </button>
                <button className="adm-btn--danger" onClick={() => handleDelete(r.id)}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default AdminReviewsPage;
